import React, { useMemo } from 'react'
import {
  Anchor,
  Breadcrumbs,
  Flex,
  Grid,
  Group,
  isLightColor,
  Paper,
  Stack,
  Text,
  useMantineTheme
} from '@mantine/core'
import { Link, useParams } from 'react-router'
import { useGetLandingPageJobQuery, useGetLandingPageQuery } from '../redux/query/LandingPagesApi.slice'
import ErrorLoading from './../../../react/core/ErrorLoading'
import Loading from './../../../react/core/Loading'
import LandingPageRecentJobs from '../components/LandingPageRecentJobs'
import ApplyButton from './../../components/ApplyButton'
import Banner from '../components/Banner'
import CompanyInformation from '../components/CompanyInformation'
import { IconMapPin } from '@tabler/icons-react'
import Badges from '../../components/Badges'
import ScrollToTop from '../../../components/ScrollToTop'
import DOMPurify from 'dompurify'
import { useMediaQuery } from '@mantine/hooks'
import ExpiredJobPosting from './ExpiredJobPosting'

const WIDTH = { base: '90%', md: '80%', lg: '60%' }
const MAX_WIDTH = '1170px'
const JOB_POSTING_COLS = { base: 12, md: 8 }
const ASIDE_COLS = { base: 12, md: 4 }

/**
 * Renders the job posting page.
 * @param {bool} visibility if this value does not match the job posting's visibility, do not display. Security is not
 * a concern here, since private job postings are still accessible unauthenticated. It just helps the component know if
 * the job posting should be rendered or show as if it was expired.
 * @returns {JSX.Element}
 */
export default function JobPosting ({ visibility }) {
  const { landing_page_identifier: landingPageIdentifier, job_posting_identifier: jobPostingIdentifier } = useParams()
  const theme = useMantineTheme()
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const { data: landingPage, isError: landingPageIsError, isLoading: landingPageLoading } = useGetLandingPageQuery(landingPageIdentifier)
  const { data: jobPosting, error: jobPostingError, isLoading: jobPostingLoading } = useGetLandingPageJobQuery({ landingPageIdentifier, jobPostingIdentifier })

  const sanitizedDescription = useMemo(() => {
    return DOMPurify.sanitize(jobPosting?.description ?? '')
  }, [jobPosting])

  if (landingPageIsError) {
    return <ErrorLoading />
  }

  if (landingPageLoading || jobPostingLoading) {
    return <Loading />
  }

  if (jobPostingError || !jobPosting || (jobPosting && jobPosting.visibility !== visibility)) {
    return <ExpiredJobPosting />
  }

  document.title = jobPosting.title

  const textColor = isLightColor(landingPage.header_banner_color) ? 'gray.8' : '#FFF'
  const primaryTextSize = isSmallScreen ? '24px' : '48px'
  const secondaryTextSize = isSmallScreen ? 'md' : 'lg'
  const locationPinSize = isSmallScreen ? 20 : 24
  const applyButtonSize = isSmallScreen ? 'sm' : 'xl'

  return (
    <>
      <ScrollToTop />
      <Flex
        gap='md'
        justify='center'
        align='center'
        direction='column'
        wrap='wrap'
        h='100%'
        pb='5em'
      >
        <Banner
          imageSrc={landingPage.header_image_banner}
          color={landingPage.header_banner_color}
        >
          <Stack align='flex-start' justify='center' w={WIDTH} maw={MAX_WIDTH}>
            <Breadcrumbs separator={<Text c={textColor} size={secondaryTextSize}>/</Text>}>
              <Anchor component={Link} to='../' c={textColor} size={secondaryTextSize}>Job postings</Anchor>
              <Text c={textColor} size={secondaryTextSize}>{jobPosting.title}</Text>
            </Breadcrumbs>
            <Text c={textColor} size={primaryTextSize} aria-label='Job posting title'>
              {jobPosting.title}
            </Text>
            <Group>
              <Badges jobPosting={jobPosting} />
              <Text c={textColor} size={secondaryTextSize}><IconMapPin size={locationPinSize} aria-label='Location of the job post' /> {jobPosting.location}</Text>
            </Group>
          </Stack>
        </Banner>
        <Grid w={WIDTH} maw={MAX_WIDTH} mt='-9.5vh'>
          <Grid.Col span={JOB_POSTING_COLS}>
            <Paper p='xl'>
              <Stack>
                <Text dangerouslySetInnerHTML={{ __html: sanitizedDescription }}></Text>
                <ApplyButton color={landingPage.button_background_color} href={jobPosting.apply_url} size={applyButtonSize} />
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={ASIDE_COLS}>
            <Stack>
              <ApplyButton color={landingPage.button_background_color} href={jobPosting.apply_url} visibleFrom='md' size={applyButtonSize} />
              <CompanyInformation landingPage={landingPage} />
              <LandingPageRecentJobs landingPage={landingPage} />
            </Stack>
          </Grid.Col>
        </Grid>
      </Flex>
    </>
  )
}
